import Postmate, { ParentAPI } from 'postmate';
import { waitAndBackoffRetry } from '../utils';
import { openO365LoginPopup } from './collaborationService';
import { dmCalls } from './serviceCalls/dmCalls';
import { fdmIrsCalls } from './serviceCalls/fdmIrsCalls';
import { insightsCalls } from './serviceCalls/insightsCalls';
import { sharedCalls } from './serviceCalls/sharedCalls';

/**
 * Class responsible for communicating with iframed services.
 * Supports both Node (native) and Postmate messages.
 *
 * It can send messages related to specific embedded service (e.g. CSS changes)
 * or more generic ones (e.g. authentication). It can also retrieve
 * messages from iframed service and send a response back (e.g. current Teams tab link).
 *
 * It exposes functions for handling requests from iframed service.
 * Those functions need to be assigned to ones with actual implementation.
 */
export class IframeMessageHost {
  private handshake?: Promise<ParentAPI | undefined>;

  public onAuthenticationRequested: (
    uri: string,
    onSuccess?: (_: void) => void
  ) => void = () => {};
  public onAuthenticationSuccessful: () => void = () => {};
  public onLogOutRequested: (expired?: boolean) => Promise<void> =
    async () => {};
  public onHandshakeReply: () => void = () => {};
  public onAccessTokenRequested: () => Promise<string> = async () => '';
  public onIdTokenRequested: () => Promise<string> = async () => '';
  public onTabLinkRequested: () => Promise<string> = async () => '';

  public embedPage(container: HTMLElement, url: string, timeout = 0) {
    Postmate.debug = false;
    this.handshake =
      timeout > 0
        ? waitAndBackoffRetry(
            async () => new Postmate({ container, url }),
            timeout,
            2,
            () =>
              container?.lastChild && container.removeChild(container.lastChild)
          )
        : new Postmate({ container, url });
  }

  public postMessages(serviceName: string) {
    this.handshake
      ?.then(async child => {
        if (child) {
          // Note the several calls to makeServiceCalls()
          // Probably all are not needed, but I've been going with the adage
          // that it's better to be safe than sorry:
          // there's no downside to making duplicate calls aside from possibly
          // dirtying up the DOM a bit with duplicate <style>
          await this.makeServiceCalls(child, serviceName);
          child.on('page-loaded', async _ => {
            await this.makeServiceCalls(child, serviceName);
          });
          child.on('page-onload', async _ => {
            await this.makeServiceCalls(child, serviceName);
          });
          child.on('hash-changed', async _ => {
            await this.makeServiceCalls(child, serviceName);
          });

          // Last attempts!
          setTimeout(async () => {
            await this.makeServiceCalls(child, serviceName);
          }, 2000);
          setTimeout(async () => {
            await this.makeServiceCalls(child, serviceName);
          }, 4000);
        }
      })
      .catch(() => {});
  }

  private async makeServiceCalls(child: ParentAPI, serviceName: string) {
    // Each service's calls are separated into different files for clarity.
    // There are 'shared calls' which are called for any service embedded.
    const callMap = {
      Forms: () => fdmIrsCalls({ child }),
      'Issue Resolution': () => fdmIrsCalls({ child }),
      'Deliverables Management': () => dmCalls({ child }),
      'Project Insights': () => insightsCalls({ child }),
    };

    sharedCalls({ child });
    try {
      callMap[serviceName]();
    } catch (error) {
      console.log(
        `Service: '${serviceName}' was not found in the Postmate callMap`
      );
    }
  }

  public reloadEmbeddedPage(serviceName?: string, frameUrl?: string) {
    this.handshake
      ?.then(child => {
        serviceName === 'Deliverables Management'
          ? child?.call('reloadEmbeddedPage', frameUrl)
          : child?.call('reloadEmbeddedPage');
      })
      .catch(() => {});
  }

  public clearEmbeddedPageStorage(storageType: string) {
    this.handshake
      ?.then(child => child?.call('clearStorage', storageType))
      .catch(error => console.error(error));
  }

  public listenForMessages() {
    // There are two different postMessage APIs in play here:
    // 1. Postmate - which handles non-authentication communication between frames
    // 2. Standard postMessage API calls - handles authentication-based communication between frames.
    window.addEventListener('message', async event => {
      event.data.postmate
        ? await this.handlePostmateMessages(event)
        : await this.handleNodeMessages(event);
    });
  }

  private handleNodeMessages(event: MessageEvent) {
    switch (event.data.type) {
      case 'authentication-requested': {
        return this.onAuthenticationRequested(event.data.uri);
      }
      case 'authentication-successful': {
        // Sent by child service after authentication is successful.
        // Should be sent from a pop-up.
        return this.onAuthenticationSuccessful();
      }
      case 'log-out-requested': {
        // Not using outside of Share, but if the child service wants to log out
        // instead of redirecting to log out, we handle this event.
        return this.onLogOutRequested();
      }
    }
  }

  private async handlePostmateMessages(event: MessageEvent) {
    if (!this.handshake) return;

    if (event.data.postmate === 'handshake-reply') {
      return this.onHandshakeReply();
    }

    if (event.data.postmate === 'emit') {
      switch (event.data.value.name) {
        case 'access-token-requested': {
          const child = await this.handshake;
          const accessToken = await this.onAccessTokenRequested();
          return child?.call('accessToken', accessToken);
        }
        case 'id-token-requested': {
          const child = await this.handshake;
          const idToken = await this.onIdTokenRequested();
          return child?.call('idToken', idToken);
        }
        case 'tab-link-requested': {
          const child = await this.handshake;
          const tabLink = await this.onTabLinkRequested();
          return child?.call('tabLink', tabLink);
        }
        case 'authentication-requested': {
          const child = await this.handshake;
          await new Promise(resolve =>
            this.onAuthenticationRequested(event.data.value.data, resolve)
          );
          return child?.call('authenticationSuccessful');
        }
        case 'log-out-requested': {
          const child = await this.handshake;
          await this.onLogOutRequested(false);
          return child?.call('logoutSuccessful');
        }
        case 'o365-authentication-requested': {
          const child = await this.handshake;
          const result = await openO365LoginPopup(event.data.value.data);
          return child?.call('o365AuthenticationResult', result);
        }
      }
    }
  }
}
